/**
 * Backup API service.
 *
 * Centralizes system backup API calls with consistent error handling
 * and TypeScript types.
 *
 * @module backupApi
 */

import { apiBlob, apiGet, apiPost } from './apiUtils';

export type SystemBackupRunState = 'running' | 'succeeded' | 'failed';
export type SystemBackupStatusState = 'idle' | 'running';
export type SystemBackupTrigger = 'manual' | 'scheduled';

export interface SystemBackupRunInfo {
  state: SystemBackupRunState;
  trigger: SystemBackupTrigger;
  started_at: string;
  finished_at?: string | null;
  filename?: string | null;
  path?: string | null;
  size_bytes?: number | null;
  media_file_count: number;
  deleted_backup_count: number;
  error?: string | null;
}

export interface SystemBackupStatusResponse {
  state: SystemBackupStatusState;
  backup_root: string;
  retention_days: number;
  next_scheduled_run_at?: string | null;
  current_run?: SystemBackupRunInfo | null;
  last_run?: SystemBackupRunInfo | null;
}

export interface SystemBackupTriggerResponse {
  message: string;
  state: SystemBackupStatusState;
  started_at: string;
  current_run: SystemBackupRunInfo;
}

export interface SystemBackupFileInfo {
  filename: string;
  size_bytes: number;
  created_at: string;
  modified_at: string;
  trigger?: string | null;
  media_file_count?: number | null;
  database_name?: string | null;
  manifest_available: boolean;
}

export interface SystemBackupFilesResponse {
  items: SystemBackupFileInfo[];
  total: number;
  backup_root: string;
}

export const getSystemBackupStatus =
  async (): Promise<SystemBackupStatusResponse> => {
    const result = await apiGet<SystemBackupStatusResponse>(
      '/admin/backups/system/status',
      {
        errorMessage: 'Failed to load system backup status',
        context: 'getSystemBackupStatus',
        throwOnError: true,
      },
    );
    if (!result) {
      throw new Error('Failed to load system backup status');
    }
    return result;
  };

export const runSystemBackup =
  async (): Promise<SystemBackupTriggerResponse> => {
    const result = await apiPost<SystemBackupTriggerResponse>(
      '/admin/backups/system/run',
      undefined,
      {
        errorMessage: 'Failed to start system backup',
        context: 'runSystemBackup',
        throwOnError: true,
      },
    );
    if (!result) {
      throw new Error('Failed to start system backup');
    }
    return result;
  };

export const listSystemBackupFiles =
  async (): Promise<SystemBackupFilesResponse> => {
    const result = await apiGet<SystemBackupFilesResponse>(
      '/admin/backups/system/files',
      {
        errorMessage: 'Failed to load system backup files',
        context: 'listSystemBackupFiles',
        throwOnError: true,
      },
    );
    if (!result) {
      throw new Error('Failed to load system backup files');
    }
    return result;
  };

export const downloadSystemBackupFile = async (
  filename: string,
): Promise<Blob> => {
  const blob = await apiBlob(
    `/admin/backups/system/files/${encodeURIComponent(filename)}`,
    {
      errorMessage: 'Failed to download system backup',
      context: 'downloadSystemBackupFile',
      throwOnError: true,
    },
  );
  if (!blob) {
    throw new Error('Failed to download system backup');
  }
  return blob;
};

/**
 * Format bytes to human-readable string
 *
 * @param {number} bytes - Bytes to format
 * @returns {string} Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${value} ${sizes[i]}`;
};
